import React, { useEffect, useRef, useState } from 'react';

import { useNavigation } from '@react-navigation/native';
import { useRouter } from 'expo-router';
import { StatusBar } from 'expo-status-bar';
import {
  PanResponder,
  StyleSheet,
  TouchableOpacity,
  View,
  useWindowDimensions,
} from 'react-native';
import Animated, { useAnimatedStyle, useSharedValue, withTiming } from 'react-native-reanimated';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { AccountDrawerPage } from 'pages/account';
import { primitive, spacing } from 'styles/constants';
import { TRANSITION_ANIMATION_TIME_MS } from 'utils/constants';
import { isWeb } from 'utils/environment';

const DRAWER_WIDTH = 0.9;

export default function Account() {
  const navigation = useNavigation();
  const { top, bottom } = useSafeAreaInsets();
  const router = useRouter();
  const { width } = useWindowDimensions();

  const translateX = useSharedValue(-width * DRAWER_WIDTH);
  const overlayOpacity = useSharedValue(0);
  const animatedStyle = useAnimatedStyle(() => ({
    transform: [{ translateX: translateX.value }],
  }));

  const overlayAnimatedStyle = useAnimatedStyle(() => {
    return { opacity: overlayOpacity.value };
  });

  const initialFocusRef = useRef(true);
  useEffect(() => {
    const removeBlurListener = navigation.addListener('blur', () => {
      translateX.value = withTiming(-width * DRAWER_WIDTH, {
        duration: TRANSITION_ANIMATION_TIME_MS,
      });
    });

    const removeFocusListener = navigation.addListener('focus', () => {
      if (initialFocusRef.current) {
        initialFocusRef.current = false;
        return;
      }
      translateX.value = withTiming(0, {
        duration: TRANSITION_ANIMATION_TIME_MS,
      });
    });

    return () => {
      removeBlurListener();
      removeFocusListener();
    };
  }, [navigation]);

  React.useEffect(() => {
    translateX.value = withTiming(0, { duration: TRANSITION_ANIMATION_TIME_MS });
    overlayOpacity.value = withTiming(0.5, { duration: TRANSITION_ANIMATION_TIME_MS });
  }, []);

  const handleClose = () => {
    translateX.value = withTiming(-width * DRAWER_WIDTH, {
      duration: TRANSITION_ANIMATION_TIME_MS,
    });
    overlayOpacity.value = withTiming(0, { duration: TRANSITION_ANIMATION_TIME_MS });
    setTimeout(() => {
      router.dismiss();
    }, TRANSITION_ANIMATION_TIME_MS);
  };

  // for smooth, nice looking slide out drawer
  const [show, setShow] = useState(false);
  useEffect(() => {
    setTimeout(() => {
      setShow(true);
    }, 0);
  }, []);

  const panResponder = PanResponder.create({
    onMoveShouldSetPanResponder: (evt, gestureState) =>
      Math.abs(gestureState.dx) > 10 && Math.abs(gestureState.dx) > Math.abs(gestureState.dy),
    onPanResponderMove: (evt, gestureState) => {
      if (gestureState.dx < 0) {
        translateX.value = gestureState.dx;
      }
    },
    onPanResponderRelease: (evt, gestureState) => {
      if (gestureState.dx < (-DRAWER_WIDTH * width) / 3) {
        handleClose();
      } else {
        translateX.value = withTiming(0, { duration: TRANSITION_ANIMATION_TIME_MS });
      }
    },
  });

  return (
    <View style={styles.fullScreen}>
      <StatusBar translucent />
      <Animated.View style={[styles.grayBackground, overlayAnimatedStyle]} />
      <Animated.View {...panResponder.panHandlers} style={styles.panResponderWrapper}>
        <Animated.View
          style={[
            styles.drawer,
            { paddingTop: top || spacing.$4, paddingBottom: bottom || spacing.$4 },
            animatedStyle,
          ]}
        >
          {show && <AccountDrawerPage handleClose={handleClose} />}
        </Animated.View>
        <TouchableOpacity style={styles.seeThroughBackground} onPress={handleClose} />
      </Animated.View>
    </View>
  );
}

const styles = StyleSheet.create({
  fullScreen: {
    flex: 1,
    flexDirection: 'row',
  },
  panResponderWrapper: {
    flex: 1,
    flexDirection: 'row',
  },
  grayBackground: {
    backgroundColor: 'rgba(0, 0, 0, 1)',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
  },
  drawer: {
    width: '90%',
    backgroundColor: primitive.$mayoEggWhite,
    padding: 16,
    ...(isWeb ? { maxWidth: 912 } : null),
  },
  seeThroughBackground: {
    flex: 1,
  },
});
